/**
 * Trainer-stats presentation for the Settings account card.
 *
 * The values come from GET /api/settings/all -> `trainer`, which is the
 * trainer_profiles row as stored against the account in the cloud. This module
 * is the read-back: what the profile-screenshot import actually persisted.
 */

/**
 * The `trainer` payload from GET /api/settings/all.
 *
 * Every stat is nullable and optional. The profile OCR only fills what it could
 * genuinely read from the screenshot, and older rows predate several columns —
 * so "not captured" must stay distinguishable from a real zero.
 */
export interface TrainerProfileDto {
  trainerName?: string | null;
  team?: string | null;
  level?: number | null;
  totalXp?: number | null;
  /** XP toward the next level (left number of the progress bar). */
  currentLevelXp?: number | null;
  totalKm?: number | null;
  pokemonCaught?: number | null;
  pokestopsVisited?: number | null;
  eggsHatched?: number | null;
  candyFound?: number | null;
  startDate?: string | null;
  friendsCount?: number | null;
  buddySpecies?: string | null;
  totalStardust?: number | null;
  updatedAt?: number | null;
}

const TEAM_LABEL: Record<string, string> = {
  mystic: "Mystic",
  valor: "Valor",
  instinct: "Instinct",
};

export interface TrainerStatRow {
  label: string;
  value: string;
}

/**
 * Build the display rows for the trainer-stats block, dropping anything the
 * import never captured.
 *
 * Absent stats are omitted rather than rendered as "—" or 0. The block is a
 * read-back of what actually reached the cloud, so a missing row is the honest
 * signal that a stat did not survive the scan — and a fabricated 0 for, say,
 * Pokemon caught would be worse than saying nothing. Labels are kept short
 * because the block is a two-column grid on a phone.
 *
 * Every number is grouped: these run to nine figures (total XP 251,292,073) and
 * are unreadable without separators.
 *
 * Pure; exported for tests.
 */
export function trainerStatRows(t: TrainerProfileDto | null | undefined): TrainerStatRow[] {
  if (!t) return [];
  const num = (v: number | null | undefined): string | null =>
    typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("en-US") : null;

  const rows: Array<{ label: string; value: string | null }> = [
    // "unset" is the OCR's "team colour not detected", not a team.
    { label: "Team", value: t.team && t.team !== "unset" ? (TEAM_LABEL[t.team] ?? t.team) : null },
    { label: "Level", value: num(t.level) },
    { label: "Total XP", value: num(t.totalXp) },
    { label: "Level XP", value: num(t.currentLevelXp) },
    {
      label: "Walked",
      // The only fractional stat; PoGo shows one decimal place.
      value:
        typeof t.totalKm === "number" && Number.isFinite(t.totalKm)
          ? `${t.totalKm.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`
          : null,
    },
    { label: "Caught", value: num(t.pokemonCaught) },
    { label: "PokéStops", value: num(t.pokestopsVisited) },
    { label: "Friends", value: num(t.friendsCount) },
    { label: "Eggs", value: num(t.eggsHatched) },
    { label: "Candy", value: num(t.candyFound) },
    { label: "Stardust", value: num(t.totalStardust) },
    { label: "Buddy", value: t.buddySpecies || null },
    { label: "Started", value: t.startDate || null },
  ];

  return rows.filter((r): r is TrainerStatRow => r.value != null);
}
